
import { defineComponent } from 'vue'
import { mapGetters, useStore } from 'vuex'
import btdecode from '@xunlei/bencode-worker'
import { isAndroidNative as isAndroid, isIOSNative as isIOS } from '@xunlei/universal-native-api'
import { checkValidLink, getThunderLink } from '@remote/utils'
import { MAX_URL_ADD_NUM } from '@remote/constant'
import '@remote/assets/css/upload.scss'
import DownloadPath from '@remote/components/bt-choice-pop/DownloadPath.vue'
import { usePathSelector } from '@remote/store/hezi-file/path-selector'
import { matchBatchBtLink } from '@remote/api/adapter/helper'
import { checkState } from '@remote/store/utils'
import { logFactory } from '@remote/utils/logger'
import { openLinkChoicePop } from '@remote/utils/dialog'

const log = logFactory('ParseInputText')

const errTxts = [
  `最多支持同时添加${MAX_URL_ADD_NUM}个链接`,
  '请输入正确的链接地址',
  '操作未成功，请稍后重试',
  '请选择正确的bt文件',
  '链接不能为空',
]

export default defineComponent({
  name: 'CreateDownloadTask',
  components: {
    DownloadPath,
  },
  props: {
    'close': {
      type: Function,
      default: null,
    },
  },
  setup: () => {
    document.title = '添加下载链接'
    const store = useStore()
    const { selectedPath } = usePathSelector()
    return { store, isAndroid, isIOS, selectedPath }
  },
  data () {
    return {
      inputText: '',
      error: '',
      isLoading: false,
      isSupportBEncode: true,
      links: [],
    } as {
      inputText: string
      error: string
      isLoading: boolean
      links: string[]
      isSupportBEncode: boolean
    }
  },
  computed: {
    ...mapGetters(['isFullScreen']),
    showUploadBT (): boolean {
      return !this.inputText
    },
    visibleDownloadPath () {
      const [magnets = [], downloadUrls = []] = matchBatchBtLink(this.inputText)
      if (magnets.length > 1) return true
      if (downloadUrls.length) return true
      return false
    },
  },
  async mounted () {
    try {
      this.isSupportBEncode = this.checkWebWorkerCompatibility()
    } catch (error) {
      console.log(error)
    }
    // textarea auto focus
    if (this.$refs.textarea) {
      this.$refs.textarea.focus()
      setTimeout(() => {
        this.$refs.textarea.select()
      }, 100)
    }
  },
  methods: {
    handleUploadBT () {
      const uploadInput = this.$refs.uploadInput as HTMLInputElement
      uploadInput.click()
    },
    /**
     * Taken from
     * https://stackoverflow.com/questions/62954570/javascript-feature-detect-module-support-for-web-workers
     * The WorkerType enum can only be one of "classic" or "module".
     * If an implementation checks this param it's because they support module.
     */
    checkWebWorkerCompatibility () {
      let supports = false
      const tester = {
        // eslint-disable-next-line getter-return, @typescript-eslint/ban-ts-comment
        // @ts-ignore
        get type () {
          supports = true
          return ''
        }, // it's been called, it's supported
      }
      try {
        // We use "blob://" as url to avoid an useless network request.
        // This will either throw in Chrome
        // either fire an error event in Firefox
        // which is perfect since
        // we don't need the worker to actually start,
        // checking for the type of the script is done before trying to load it.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        new Worker('blob://', tester)
      } finally {
        // eslint-disable-next-line no-unsafe-finally
        return supports
      }
    },
    // Handle user selected bt files
    async handleOnSelecteBTFiles (evt) {
      let errTxts
      for (let i = 0; i < evt.target.files.length; i++) {
        const [errrMsg, result] = await this.parseBtFile2BTLink(evt.target.files[i])
        if (result) this.inputText = this.inputText + '\n' + result
        if (errrMsg) errTxts = errrMsg
      }

      if (this.inputText) {
        this.handleOnSubmitLinks()
      } else {
        this.error = errTxts
      }
      const uploadInput = this.$refs.uploadInput as HTMLInputElement
      uploadInput.value = ''
    },
    // Parse bt file to bt link
    async parseBtFile2BTLink (file: File) {
      return new Promise((resolve) => {
        const fileName = file.name
        let errrMsg
        let result
        if (fileName.substring(fileName.lastIndexOf('.')) !== '.torrent') return (this.error = errTxts[3])
        const reader = new FileReader()
        reader.readAsArrayBuffer(file)
        reader.onload = async (e: ProgressEvent<FileReader>) => {
          try {
            result = await btdecode(e?.target?.result as ArrayBuffer)
          } catch (error) {
            errrMsg = errTxts[3]
            log('ParseBTFile2BTLinkFail', fileName)
          } finally {
            resolve([errrMsg, result])
          }
        }
      })
    },
    // Go to home or close pop window
    handleOnBack () {
      if (typeof this.close === 'function') {
        this.close()
      } else {
        this.$router?.replace({ path: '/home', query: this.$route.query })
        this.inputText = ''
      }
    },
    async handleOnSubmitLinks () {
      if (this.inputText === '') return this.error = errTxts[4]
      if (checkState()) return
      log('UserInputText', this.inputText)
      // Split with \n
      let allLinks = this.inputText.split('\n').filter((v) => v && v.length > 0)
      // Remove duplication
      allLinks = Array.from(new Set(allLinks))
      let links = allLinks.map((v) => v && getThunderLink(v))
      links = links.filter((v) => v && checkValidLink(v))

      log('WillSubmittedLinks', links)
      if (links.length === 0) {
        log('InvalidLinks')
        return (this.error = errTxts[1])
      }

      if (links.length > MAX_URL_ADD_NUM) {
        log('LinksNumsOverUpperLimit')
        return (this.error = errTxts[0])
      }
      if (links.length > 1) {
        openLinkChoicePop({ urls: links })
      } else {
        this.store.dispatch('getResource', {
          urls: links.join('\n'),
          downloadPath: this.selectedPath.value.path,
        })
      }
      return this.handleOnBack()
    },
  },
})
